import { Drawer, Paragraph, Select } from '@tmov/design-system';
import { Form, notification } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { carregaCidades } from '../../../../helpers/cidades';
import { carregaEstadosBrasil } from '../../../../helpers/estados';
import { toTitleCase } from '../../../../helpers/formatador';
import { searchProdutosAts } from '../../../../helpers/produtos';
import { DrawerFooter } from './DrawerFooter';
import { DrawerHeader } from './DrawerHeader';

function FiltersDrawer({
  drawerActive,
  filters,
  setDrawerActive,
  updateFilters,
}) {
  const [form] = Form.useForm();

  const [produtoSearch, setProdutoSearch] = useState('');
  const [produtoValues, setProdutoValues] = useState([]);
  const [produtoLoading, setProdutoLoading] = useState(false);
  const produtoOptions = useMemo(
    () =>
      produtoValues?.length > 0
        ? produtoValues.map(item => ({
            id: item.IdProduto,
            value: toTitleCase(item.Descricao),
          }))
        : [],
    [produtoValues]
  );
  const handleChangeProdutoSearch = useDebouncedCallback(searchValue => {
    setProdutoSearch(searchValue);
  }, 300);

  const [cidadeOrigemSearch, setCidadeOrigemSearch] = useState('');
  const [cidadeOrigemValues, setCidadeOrigemValues] = useState([]);
  const [cidadeOrigemLoading, setCidadeOrigemLoading] = useState(false);
  const cidadeOrigemOptions = useMemo(
    () =>
      cidadeOrigemValues?.length > 0
        ? cidadeOrigemValues.map(item => ({
            id: item.idCidade,
            value: toTitleCase(item.cidadeNome),
          }))
        : [],
    [cidadeOrigemValues]
  );
  const handleChangeCidadeOrigemSearch = useDebouncedCallback(searchValue => {
    setCidadeOrigemSearch(searchValue);
  }, 300);

  const [cidadeDestinoSearch, setCidadeDestinoSearch] = useState('');
  const [cidadeDestinoValues, setCidadeDestinoValues] = useState([]);
  const [cidadeDestinoLoading, setCidadeDestinoLoading] = useState(false);
  const cidadeDestinoOptions = useMemo(
    () =>
      cidadeDestinoValues?.length > 0
        ? cidadeDestinoValues.map(item => ({
            id: item.idCidade,
            value: toTitleCase(item.cidadeNome),
          }))
        : [],
    [cidadeDestinoValues]
  );
  const handleChangeCidadeDestinoSearch = useDebouncedCallback(searchValue => {
    setCidadeDestinoSearch(searchValue);
  }, 300);

  const [estadoValues, setEstadoValues] = useState([]);
  const [estadoLoading, setEstadoLoading] = useState(false);
  const estadoOptions = useMemo(
    () =>
      estadoValues?.length > 0
        ? estadoValues.map(item => ({
            id: item.IdEstado,
            value: toTitleCase(item.Nome),
          }))
        : [],
    [estadoValues]
  );

  const clearFilters = () => {
    updateFilters({
      produto: null,
      cidadeOrigem: null,
      cidadeDestino: null,
      estadoOrigem: null,
      estadoDestino: null,
      // carregamento: null,
      // periodo: null,
    });
    form.resetFields();
  };

  const onSubmit = values => {
    updateFilters({ ...values });
    setDrawerActive(false);
  };

  const onFinishFailed = errorInfo => {
    notification.error({
      message: 'Ocorreu um erro ao aplicar os valores do filtro',
      description: errorInfo,
    });
  };

  useEffect(() => {
    const loadProdutoValues = async () => {
      await searchProdutosAts(
        produtoSearch,
        setProdutoValues,
        setProdutoLoading
      );
    };
    loadProdutoValues();
  }, [produtoSearch]);

  useEffect(() => {
    const loadCidadesOrigem = async () => {
      await carregaCidades(
        cidadeOrigemSearch,
        setCidadeOrigemValues,
        setCidadeOrigemLoading
      );
    };
    loadCidadesOrigem();
  }, [cidadeOrigemSearch]);

  useEffect(() => {
    const loadCidadesDestino = async () => {
      await carregaCidades(
        cidadeDestinoSearch,
        setCidadeDestinoValues,
        setCidadeDestinoLoading
      );
    };
    loadCidadesDestino();
  }, [cidadeDestinoSearch]);

  useEffect(() => {
    const loadEstados = async () => {
      await carregaEstadosBrasil(setEstadoValues, setEstadoLoading);
    };
    loadEstados();
  }, []);

  return (
    <Drawer
      header={<DrawerHeader clearFilters={clearFilters} />}
      footer={<DrawerFooter submitForm={form.submit} />}
      active={drawerActive}
      updateActive={setDrawerActive}
    >
      <Form form={form} onFinish={onSubmit} onFinishFailed={onFinishFailed}>
        <Paragraph
          variant="p2"
          fontWeight="semibold"
          style={{ marginBottom: '4px' }}
        >
          Produto
        </Paragraph>
        <Paragraph variant="p2">Filtre por um produto específico.</Paragraph>
        <Form.Item
          name="produto"
          valuePropName="selectedOption"
          initialValue={filters.produto}
        >
          <Select
            id="produto"
            label="Produto"
            onChangeSearch={handleChangeProdutoSearch}
            options={produtoOptions}
            isLoading={produtoLoading}
            width="320px"
            data-testid="produto-select"
            noResultsText={
              produtoSearch.length < 3
                ? 'Insira pelo menos 3 caracteres para pesquisar'
                : 'Não há itens que correspondem à pesquisa'
            }
          />
        </Form.Item>
        <br />
        <Paragraph
          variant="p2"
          fontWeight="semibold"
          style={{ marginBottom: '4px' }}
        >
          Cidade
        </Paragraph>
        <Paragraph variant="p2">
          Filtre por cidade de origem e/ou destino.
        </Paragraph>
        <Form.Item
          name="cidadeOrigem"
          valuePropName="selectedOption"
          initialValue={filters.cidadeOrigem}
        >
          <Select
            id="cidadeOrigem"
            label="Cidade de origem"
            onChangeSearch={handleChangeCidadeOrigemSearch}
            options={cidadeOrigemOptions}
            isLoading={cidadeOrigemLoading}
            width="320px"
            data-testid="cidade-origem-select"
            noResultsText={
              cidadeOrigemSearch.length < 3
                ? 'Insira pelo menos 3 caracteres para pesquisar'
                : 'Não há itens que correspondem à pesquisa'
            }
          />
        </Form.Item>
        <Form.Item
          name="cidadeDestino"
          valuePropName="selectedOption"
          initialValue={filters.cidadeDestino}
        >
          <Select
            id="cidadeDestino"
            label="Cidade de destino"
            onChangeSearch={handleChangeCidadeDestinoSearch}
            options={cidadeDestinoOptions}
            isLoading={cidadeDestinoLoading}
            width="320px"
            data-testid="cidade-destino-select"
            noResultsText={
              cidadeDestinoSearch.length < 3
                ? 'Insira pelo menos 3 caracteres para pesquisar'
                : 'Não há itens que correspondem à pesquisa'
            }
          />
        </Form.Item>
        <br />
        <Paragraph
          variant="p2"
          fontWeight="semibold"
          style={{ marginBottom: '4px' }}
        >
          Estado
        </Paragraph>
        <Paragraph variant="p2">
          Filtre por estado de origem e/ou destino.
        </Paragraph>
        <Form.Item
          name="estadoOrigem"
          valuePropName="selectedOption"
          initialValue={filters.estadoOrigem}
        >
          <Select
            label="Estado de origem"
            options={estadoOptions}
            isLoading={estadoLoading}
            width="320px"
            data-testid="estado-origem-select"
          />
        </Form.Item>
        <Form.Item
          name="estadoDestino"
          valuePropName="selectedOption"
          initialValue={filters.estadoDestino}
        >
          <Select
            label="Estado de destino"
            options={estadoOptions}
            isLoading={estadoLoading}
            width="320px"
            data-testid="estado-destino-select"
          />
        </Form.Item>
        {/*
            <Paragraph
              variant="p2"
              fontWeight="semibold"
              style={{ marginBottom: '4px' }}
            >
              Carregamento
            </Paragraph>
            <Paragraph variant="p2">
              Filtre por data de início do carregamento.
            </Paragraph>
            <Form.Item name="carregamento">
              <TextField label="Início do carregamento" trailingIcon={Search} />
            </Form.Item>
            <Paragraph
              variant="p2"
              fontWeight="semibold"
              style={{ marginBottom: '4px' }}
            >
              Período
            </Paragraph>
            <Paragraph variant="p2">
              Filtre por demandas de um período específico.
            </Paragraph>
            <Form.Item name="periodo">
              <TextField label="Selecionar período" trailingIcon={Search} />
            </Form.Item>
          */}
      </Form>
    </Drawer>
  );
}

const filter = PropTypes.shape({
  id: PropTypes.number,
  value: PropTypes.string,
});

FiltersDrawer.propTypes = {
  drawerActive: PropTypes.bool.isRequired,
  filters: PropTypes.shape({
    produto: filter,
    cidadeOrigem: filter,
    cidadeDestino: filter,
    estadoOrigem: filter,
    estadoDestino: filter,
    // carregamento: PropTypes.string.isRequired,
    // periodo: PropTypes.string.isRequired,
  }).isRequired,
  setDrawerActive: PropTypes.func.isRequired,
  updateFilters: PropTypes.func.isRequired,
};

export default FiltersDrawer;
